import { api } from "./api";
import { setCookie, getCookie, deleteCookie } from "./cookie-utils";
import type {
  LoginCredentials,
  LoginResponse,
  RegisterCredentials,
  RegisterResponse,
  LogoutResponse,
  MeResponse,
  User,
} from "@/types/auth";

const TOKEN_KEY = "santafe_conecta_token";
const USER_KEY = "santafe_conecta_user";

export const authService = {
  // Login
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>("/auth/login", credentials);

    // Salvar token e user em cookies
    if (response.data.token) {
      this.setToken(response.data.token);
    }
    if (response.data.user) {
      this.setUser(response.data.user);
    }

    return response.data;
  },

  // Register
  async register(
    credentials: RegisterCredentials,
    isCompany: boolean = false
  ): Promise<RegisterResponse> {
    // Escolher endpoint baseado no tipo de cadastro
    const endpoint = isCompany ? "/auth/register" : "/users/common";

    const response = await api.post<RegisterResponse>(endpoint, credentials);

    // Salvar token e user em cookies se fornecido
    if (response.data.token) {
      this.setToken(response.data.token);
    }
    if (response.data.user) {
      this.setUser(response.data.user);
    }

    return response.data;
  },

  // Logout
  async logout(): Promise<void> {
    try {
      // Tentar fazer logout na API, mas não bloquear se falhar
      await api.post<LogoutResponse>("/auth/logout");
    } catch (error) {
      // Ignorar erros da API no logout
      console.warn("Erro ao fazer logout na API:", error);
    } finally {
      // Sempre limpar dados locais, mesmo se a API falhar
      this.clearAuth();
    }
  },

  // Obter dados do usuário atual (sempre busca da API)
  async me(): Promise<User> {
    try {
      const response = await api.get<MeResponse>("/auth/me");
      // Atualizar cookie do user
      if (response.data.user) {
        this.setUser(response.data.user);
      }
      return response.data.user;
    } catch (error: unknown) {
      // Se for erro de rede, limpar autenticação
      const err = error as { isNetworkError?: boolean; code?: string };
      if (err.isNetworkError || err.code === "ERR_NETWORK") {
        this.clearAuth();
        throw new Error(
          "Não foi possível conectar ao servidor. Você foi desconectado."
        );
      }
      throw error;
    }
  },

  // Gerenciamento de token
  setToken(token: string): void {
    setCookie(TOKEN_KEY, token, 7);
  },

  getToken(): string | null {
    return getCookie(TOKEN_KEY);
  },

  removeToken(): void {
    deleteCookie(TOKEN_KEY);
  },

  // Gerenciamento de user
  setUser(user: User): void {
    setCookie(USER_KEY, JSON.stringify(user), 7);
  },

  getUser(): User | null {
    const userData = getCookie(USER_KEY);
    if (!userData) return null;
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  },

  removeUser(): void {
    deleteCookie(USER_KEY);
  },

  // Verificar autenticação
  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  // Limpar autenticação
  clearAuth(): void {
    this.removeToken();
    this.removeUser();
  },
};
